import { useState, useMemo, useEffect, useCallback } from 'react';

import { Eye, Search, X } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';

import { Pagination } from '@/components/shared/Pagination';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useLeads } from '@/hooks/useLeads';
import { usePageTitle } from '@/hooks/usePageTitle';
import { formatDateTime } from '@/utils/format';

import type { LeadWithRelations, LeadQuery, FinancePreference } from '@repo/shared-types';

const FINANCE_PREFERENCES: FinancePreference[] = ['FINANCE', 'OUTRIGHT', 'UNSURE'];

export const LeadsPage = () => {
  usePageTitle('Leads');
  const [searchParams, setSearchParams] = useSearchParams();
  const limit = 10;

  // Read state from URL
  const financePreference = (searchParams.get('finance_preference') as FinancePreference) || '';
  const campaignId = searchParams.get('campaign_id') || '';
  const page = Number(searchParams.get('page')) || 1;

  // Local state for search input (for debouncing)
  const [searchInput, setSearchInput] = useState(searchParams.get('search') || '');
  const [debouncedSearch, setDebouncedSearch] = useState(searchParams.get('search') || '');

  // Update URL params helper
  const updateParams = useCallback(
    (updates: Record<string, string | null>) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        for (const [key, value] of Object.entries(updates)) {
          if (value) {
            next.set(key, value);
          } else {
            next.delete(key);
          }
        }
        return next;
      });
    },
    [setSearchParams],
  );

  // Debounce search input and sync to URL
  useEffect(() => {
    const timer = setTimeout(() => {
      const searchChanged = debouncedSearch !== searchInput;
      setDebouncedSearch(searchInput);
      updateParams({
        search: searchInput || null,
        ...(searchChanged && { page: null }),
      });
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput, updateParams, debouncedSearch]);

  // Build query from state
  const query = useMemo(() => {
    const q: Partial<LeadQuery> = { page, limit };
    if (debouncedSearch) q.search = debouncedSearch;
    if (financePreference) q.finance_preference = financePreference;
    if (campaignId) q.campaign_id = campaignId;
    return q;
  }, [debouncedSearch, financePreference, campaignId, page, limit]);

  const { data, isLoading } = useLeads(query);
  const leads = data?.data ?? [];
  const pagination = data?.pagination;

  const clearFilters = () => {
    setSearchInput('');
    setDebouncedSearch('');
    setSearchParams(new URLSearchParams());
  };

  const hasActiveFilters = searchInput || financePreference || campaignId;

  return (
    <div className="space-y-8">
      <header className="space-y-2">
        <p className="text-muted-foreground text-sm tracking-[0.3em] uppercase">Leads</p>
        <h1 className="text-3xl font-bold tracking-tight">Customer leads</h1>
        <p className="text-muted-foreground max-w-3xl">
          View all customer leads captured from calls, including contact information, vehicle
          interests, and preferences.
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Leads</CardTitle>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="mb-6 flex flex-wrap items-end gap-4">
            <div className="min-w-50 flex-1">
              <label className="text-muted-foreground mb-1 block text-sm font-medium">Search</label>
              <div className="relative">
                <Search className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Search by name or phone..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="focus:ring-primary w-full rounded-md border py-2 pr-3 pl-9 text-sm focus:ring-2 focus:outline-none"
                />
              </div>
            </div>
            <div className="min-w-45">
              <label
                htmlFor="finance-filter"
                className="text-muted-foreground mb-1 block text-sm font-medium"
              >
                Finance Preference
              </label>
              <select
                id="finance-filter"
                value={financePreference}
                onChange={(e) =>
                  updateParams({ finance_preference: e.target.value || null, page: null })
                }
                className="focus:ring-primary w-full rounded-md border px-3 py-2 text-sm focus:ring-2 focus:outline-none"
              >
                <option value="">All preferences</option>
                {FINANCE_PREFERENCES.map((fp) => (
                  <option key={fp} value={fp}>
                    {fp}
                  </option>
                ))}
              </select>
            </div>
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                <X className="mr-1 h-4 w-4" />
                Clear filters
              </Button>
            )}
          </div>

          {isLoading ? (
            <div className="flex min-h-[200px] items-center justify-center">
              <div className="text-center">
                <div className="border-primary mx-auto h-8 w-8 animate-spin rounded-full border-b-2" />
                <p className="text-muted-foreground mt-2 text-sm">Loading leads...</p>
              </div>
            </div>
          ) : leads.length === 0 ? (
            <div className="text-muted-foreground rounded-lg border border-dashed p-6 text-center text-sm">
              No leads found. {hasActiveFilters ? 'Try adjusting your filters.' : ''}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="text-muted-foreground text-left text-xs tracking-wide uppercase">
                    <th className="border-b px-3 py-2">Customer Name</th>
                    <th className="border-b px-3 py-2">Phone Number</th>
                    <th className="border-b px-3 py-2">Vehicle Interest</th>
                    <th className="border-b px-3 py-2">Budget</th>
                    <th className="border-b px-3 py-2">Finance Preference</th>
                    <th className="border-b px-3 py-2">Showroom Interest</th>
                    <th className="border-b px-3 py-2">Campaign</th>
                    <th className="border-b px-3 py-2">Created At</th>
                    <th className="border-b px-3 py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((lead: LeadWithRelations) => (
                    <tr key={lead.id} className="align-top">
                      <td className="border-b px-3 py-2 font-medium">{lead.customer_name}</td>
                      <td className="border-b px-3 py-2">{lead.phone_number}</td>
                      <td className="border-b px-3 py-2">{lead.vehicle_interest || '-'}</td>
                      <td className="border-b px-3 py-2">{lead.budget || '-'}</td>
                      <td className="border-b px-3 py-2">
                        {lead.finance_preference ? (
                          <span className="bg-primary/10 text-primary rounded-full px-2 py-1 text-xs font-medium">
                            {lead.finance_preference}
                          </span>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td className="border-b px-3 py-2">
                        {lead.showroom_interest === true ? (
                          <span className="rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-700">
                            Yes
                          </span>
                        ) : lead.showroom_interest === false ? (
                          <span className="rounded-full bg-red-100 px-2 py-1 text-xs font-medium text-red-700">
                            No
                          </span>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td className="border-b px-3 py-2 text-sm">{lead.campaign?.name || '-'}</td>
                      <td className="border-b px-3 py-2">{formatDateTime(lead.created_at)}</td>
                      <td className="border-b px-3 py-2">
                        <div className="flex justify-end gap-2">
                          <Button asChild size="sm" variant="secondary">
                            <Link to={`/leads/${lead.id}`}>
                              <Eye className="h-4 w-4" />
                              View
                            </Link>
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {pagination && (
            <Pagination
              page={pagination.page}
              totalPages={pagination.totalPages}
              total={pagination.total}
              limit={pagination.limit}
              onPageChange={(p) => updateParams({ page: p > 1 ? String(p) : null })}
              resourceName="leads"
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
};
